import React, { memo, useEffect, useState, useMemo } from 'react';
import { useFormik } from 'formik';
import { useNavigate, useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { dates, formats, validators } from 'investira.sdk';
import {
  Button,
  Stack,
  InputAdornment,
  MenuItem,
  TextField,
  Icon,
  LoadingButton,
  Skeleton,
  Autocomplete,
  Typography,
  Alert,
  ListItemText,
  Chip,
  Tabs,
  Tab,
  Box,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Tooltip,
  IconButton,
  CircularProgress,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormControl
} from 'investira.react.components';
import { VALIDATIONS } from '../../../const';
import { useRelatorioContext } from '../../../contexts/RelatorioContext';
import services from '../../../services';
import withResponseHandling from '../../../hoc/withResponseHandling';
import withSaveParams from '../../../hoc/withSaveParams';
import { CalendarPosicoes } from '../../../components/molecules';
import { ASSINATURA_ESTAGIO, CARTEIRA_TIPO } from '../../../enums';
import { useSelector } from 'react-redux';

const VALIDATION_SCHEMA = Yup.object().shape({
  tipo_selecao: Yup.string().oneOf(['carteira', 'portfolio']).required(VALIDATIONS.REQUIRED),
  carteira_id: Yup.string().when('tipo_selecao', {
    is: 'carteira',
    then: Yup.string().required(VALIDATIONS.REQUIRED),
    otherwise: Yup.string().nullable()
  }),
  portfolio_id: Yup.string().when('tipo_selecao', {
    is: 'portfolio',
    then: Yup.string().required(VALIDATIONS.REQUIRED),
    otherwise: Yup.string().nullable()
  }),
  data: Yup.string().when('tipo_selecao', {
    is: 'carteira',
    then: Yup.string().required(VALIDATIONS.REQUIRED).nullable(),
    otherwise: Yup.string().nullable()
  }),
  enquadramento_id: Yup.string().required(VALIDATIONS.REQUIRED)
});

const EnquadramentoParams = memo(props => {
  const { state, actions } = useRelatorioContext();

  const { data: xData, current: xCurrent } = useSelector(store => store.entidade);

  // Tenta recuperar a propriedade assinaturas da configuração da entidade
  const xAssinatura = xData.find(xEntidade => {
    return xEntidade.entidade_id === xCurrent.default_entidade_id;
  })?.config?.assinatura;

  const navigate = useNavigate();
  const location = useLocation();

  const storedParams = props.storedParams('enquadramento', props.lastOutput);

  const [datas, setDatas] = useState([]);
  const [enquadramentos, setEnquadramentos] = useState([]);

  const [isLoadingEnquadramentos, setIsLoadingEnquadramentos] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isLoadingCarteiras, setIsLoadingCarteiras] = useState(true);

  const xInitialValues = {
    tipo_selecao: storedParams.portfolio_id ? 'portfolio' : 'carteira',
    carteira_id: '',
    portfolio_id: '',
    data: storedParams.portfolio_id ? null : storedParams.data || null,
    aberta: true,
    enquadramento_id: '',
    consolidacao_carteira_tipo: storedParams.portfolio_id ? 'F' : 'I',
    consolidacao_por_emissor: storedParams.consolidacao_por_emissor || 'I',
    carteiras: []
  };

  const formik = useFormik({
    initialValues: xInitialValues,
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: (pValues, pActions) => {
      const {
        aberta,
        carteira_id,
        portfolio_id,
        tipo_selecao,
        carteiras,
        consolidacao_por_emissor,
        ...restValues
      } = pValues;

      const xPayloads = {
        carteira: {
          aberta,
          carteira_id,
          ...restValues,
          // TODO: Remover quando o option (consolidacao_por_emissor) for retirado
          consolidacao_carteira_tipo: consolidacao_por_emissor
        },
        portfolio: { portfolio_id, ...restValues }
      };

      const xPayload = xPayloads[tipo_selecao];

      pActions.setSubmitting(true);

      services.outputs.enquadramento(
        xPayload,
        rRes => {
          const { output_cache_id } = rRes.data;
          navigate(`${location.pathname}/${output_cache_id}`);
          actions.handleNewConsultCancel();
          props.saveParams('enquadramento', pValues);
        },
        rErr => {
          props.responseErrorHandling(rErr);
        },
        () => {
          pActions.setSubmitting(false);
        }
      );
    }
  });

  function handleChangeTab(_pEvent, pValue) {
    if (!pValue) return;

    formik.setFieldValue('tipo_selecao', pValue);
    formik.setFieldValue('carteira_id', '');
    formik.setFieldValue('portfolio_id', '');
    formik.setFieldValue('consolidacao_carteira_tipo', pValue === 'portfolio' ? 'F' : 'I');
    formik.setFieldValue('data', null);
    formik.setFieldValue('carteiras', []);

    setDatas([]);

    // Cancela os rquest de datas posição ao trocar de aba
    pValue === 'portfolio' && services.posicoes.cancel?.datas?.cancel();
    pValue === 'carteira' && services.portfolios.cancel?.datas?.cancel();

    services.enquadramentos.cancel.list.cancel();
  }

  // function handleChangePorfolioPosicao(pPayload) {
  //   formik.setFieldValue('data', pPayload.dateFormated);
  //   formik.setFieldValue('carteiras', []);
  // }

  function handleChangeEnquadramento(_e, pValue) {
    formik.setFieldValue('enquadramento_id', pValue?.enquadramento_id || '');
  }

  function handleClose() {
    actions.handleNewConsultCancel();
    setTimeout(() => props.setMaxWidth('sm'), 300);
  }

  function updatePortfolioCarteiras(pPortfolioId, pData) {
    setIsLoadingCarteiras(true);
    services.portfolios.carteiraDatas(
      {
        portfolio_id: pPortfolioId,
        data_referencia: pData
      },
      rRes => {
        const xCarteiras = rRes.data[0].carteiras;
        formik.setFieldValue('carteiras', xCarteiras);
      },
      rErr => {
        props.responseErrorHandling(rErr);
      },
      () => {
        setIsLoadingCarteiras(false);
      }
    );
  }

  const {
    tipo_selecao,
    data,
    portfolio_id,
    carteira_id,
    consolidacao_carteira_tipo,
    consolidacao_por_emissor,
    carteiras
  } = formik.values;

  useEffect(() => {
    const xTipoSelecaoMaxWidth = {
      carteira: 'sm',
      portfolio: 'sm'
    };

    props.setMaxWidth(xTipoSelecaoMaxWidth[tipo_selecao]);
  }, [tipo_selecao]);

  useEffect(() => {
    const xValues = storedParams;
    if (!validators.isEmpty(xValues)) {
      const { carteira_id, portfolio_id } = xValues;

      formik.setFieldValue('tipo_selecao', tipo_selecao);
      formik.setFieldValue('carteira_id', carteira_id);
      formik.setFieldValue('portfolio_id', portfolio_id);
      formik.setFieldValue('consolidacao_carteira_tipo', consolidacao_carteira_tipo);
      formik.setFieldValue('consolidacao_por_emissor', consolidacao_por_emissor);
    }
  }, []);

  useEffect(() => {
    services.enquadramentos.list(
      { data: formik.values.data },
      rRes => {
        const xEnquadramentos = Object.values(rRes.data)
          .map(xItem => {
            return {
              ...xItem,
              label: `${xItem.enquadramento}`
            };
          })
          .filter(xEnquaramento =>
            // TODO: O filtro deverá vir do back. remover quando existir a rota que retorna os enquadramentos já aprovados
            // Caso a entidade possua controle de assinatura, filtra apenas os enquadramentos aprovados
            xAssinatura
              ? xEnquaramento?.assinaturas?.assinatura_estagio === ASSINATURA_ESTAGIO.AP.id
              : true
          );

        setEnquadramentos(xEnquadramentos);

        const xValues = storedParams;
        if (!validators.isEmpty(xValues)) {
          const { enquadramento_id } = xValues;
          formik.setFieldValue('enquadramento_id', enquadramento_id);
        }
      },
      rErr => {
        props.responseErrorHandling(rErr);
      },
      () => {
        setIsLoadingEnquadramentos(false);
      }
    );
  }, [data]);

  useEffect(() => {
    if (state.isDialogOpen && tipo_selecao === 'carteira' && carteira_id) {
      setDatas([]);
      setIsLoadingData(true);

      formik.values.carteira_id &&
        services.posicoes.datas(
          {
            sort: 'data DESC',
            carteira_id: formik.values.carteira_id
          },
          rRes => {
            const xDates = rRes.data.map(xPosicao => {
              return {
                value: formats.formatDateCustom(dates.toDate(xPosicao.data), 'YYYY-MM-DD'),
                label: formats.formatDateCustom(dates.toDate(xPosicao.data), 'DD/MM/YYYY'),
                aberta: xPosicao.aberta
              };
            });

            setDatas(xDates);

            const xValues = storedParams;

            if (!validators.isEmpty(xValues) && formik.values.carteira_id === xValues.carteira_id) {
              const { data } = xValues;
              formik.setFieldValue('data', data);
            } else {
              formik.setFieldValue('data', xDates[0].value);
            }
          },
          rErr => {
            props.responseErrorHandling(rErr);
          }
        );
    }

    if (state.isDialogOpen && tipo_selecao === 'portfolio' && portfolio_id) {
      setDatas([]);
      formik.setFieldValue('carteiras', []);
      setIsLoadingData(true);

      services.portfolios.datas(
        {
          portfolio_id: formik.values.portfolio_id
        },
        rRes => {
          const xDatas = rRes.data[0].datas;
          if (xDatas) {
            const xPosicoes = xDatas.map(xData => {
              return { data: xData };
            });

            setDatas(xPosicoes);
            //formik.setFieldValue('data', xDatas[0]);

            const xValues = storedParams;
            let xData = null;

            if (
              !validators.isEmpty(xValues) &&
              formik.values.portfolio_id === xValues.portfolio_id
            ) {
              xData = xValues.data;
            } else {
              xData = xDatas[0];
            }

            xData && formik.setFieldValue('data', xData);
            //xData && updatePortfolioCarteiras(formik.values.portfolio_id, xData);
          }
        },
        rErr => {
          props.responseErrorHandling(rErr);
        },
        () => {
          setIsLoadingData(false);
        }
      );
    }
  }, [state.isDialogOpen, carteira_id, portfolio_id, tipo_selecao]);

  useEffect(() => {
    if (formik.values.tipo_selecao === 'portfolio' && formik.values.data) {
      updatePortfolioCarteiras(formik.values.portfolio_id, formik.values.data);
    }
  }, [tipo_selecao, portfolio_id, data]);

  const xEnquadramentoValue =
    enquadramentos.filter(
      xEnquadramento => xEnquadramento.enquadramento_id === formik.values.enquadramento_id
    )[0] || null;

  const xCarteiras = useMemo(
    () => props.carteiras.slice().sort((a, b) => a.carteira.localeCompare(b.carteira)),
    [props.carteiras]
  );

  const xPortfolios = useMemo(
    () => (props.portfolios || []).slice().sort((a, b) => a.portfolio.localeCompare(b.portfolio)),
    [props.portfolios]
  );

  const xDataSelected = useMemo(
    () => datas.find(xData => xData.value === formik.values.data),
    [datas, formik.values.data]
  );

  const xDataSelectedSync = useMemo(() => {
    if (tipo_selecao === 'carteira' || carteiras.length === 0) return null;
    return carteiras.every(
      xCarteira => xCarteira.data === formik.values.data && xCarteira.data_sync
    );
  }, [carteiras]);

  const xIsShowDetails = useMemo(
    () => carteiras.some(xCarteira => !validators.isTrue(xCarteira.data_sync)),
    [carteiras]
  );

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      <Stack sx={{ marginBottom: 2, py: 1 }} spacing={2}>
        {/* Tabs Carteira | Portfolio */}

        <Stack flexGrow={1}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={formik.values.tipo_selecao || 'carteira'}
              onChange={handleChangeTab}
              sx={{ alignSelf: 'center' }}>
              <Tab value="carteira" label="Carteira" />
              <Tab value="portfolio" label="Portfólio" />
            </Tabs>
          </Box>
        </Stack>

        {/* Seleção de carteira ou portfolio */}
        <Stack flexGrow={1}>
          {formik.values.tipo_selecao === 'carteira' ? (
            <TextField
              id="carteira_id"
              name="carteira_id"
              label="Carteira"
              variant="outlined"
              value={formik.values.carteira_id}
              onChange={formik.handleChange}
              select
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon iconName="wallet" size={21} />
                  </InputAdornment>
                )
              }}>
              {xCarteiras.map(xOption => (
                <MenuItem key={xOption.carteira_id} value={xOption.carteira_id}>
                  {xOption.carteira}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              id="portfolio_id"
              name="portfolio_id"
              label="Portfólio"
              variant="outlined"
              value={formik.values.portfolio_id}
              onChange={formik.handleChange}
              select
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon iconName="wallet" size={21} />
                  </InputAdornment>
                )
              }}>
              {xPortfolios.map(xOption => (
                <MenuItem key={xOption.portfolio_id} value={xOption.portfolio_id}>
                  {xOption.portfolio}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>

        {/* Posições de carteiras */}
        {formik.values.carteira_id && datas && (
          <>
            <Stack direction="row" spacing={2}>
              <Stack flexGrow={1} flexBasis={1}>
                {validators.isEmpty(formik.values.data) ? (
                  <Skeleton variant="rounded" animation="wave" height={65} />
                ) : (
                  <TextField
                    id="data"
                    name="data"
                    label="Posição de"
                    variant="outlined"
                    value={formik.values.data}
                    onChange={formik.handleChange}
                    sx={{
                      '& .MuiChip-root': {
                        display: 'none'
                      }
                    }}
                    select
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon iconName="calendar" size={21} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {xDataSelected?.aberta && (
                            <Chip
                              icon={<Icon iconName="box_3d_open" size={18} />}
                              label={'Aberta'}
                              color="warning"
                              size="small"
                              sx={{ px: 1, display: 'flex !important', mr: 4 }}
                              variant="filled"
                            />
                          )}
                        </InputAdornment>
                      )
                    }}
                    error={Boolean(formik.errors.data)}
                    helperText={formik.errors.data}
                    disabled={datas.length <= 1}>
                    {datas.map(xOption => (
                      <MenuItem key={xOption.value} value={xOption.value}>
                        <ListItemText>{xOption.label}</ListItemText>
                        {xOption.aberta && (
                          <Chip
                            icon={<Icon iconName="box_3d_open" size={18} />}
                            label={'Aberta'}
                            color="warning"
                            size="small"
                            sx={{ px: 1 }}
                            variant="filled"
                          />
                        )}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>
            </Stack>
          </>
        )}

        {formik.values.portfolio_id && (
          <Stack
            direction="column"
            gap={3}
            //sx={{ minHeight: 336 }}
          >
            {/* Posições de portolio */}
            {/* {datas && (
              <>
                <Stack direction="row" spacing={2}>
                  <Stack flexGrow={1} flexBasis={1} justifyContent="center" alignItems="center">
                    {isLoadingData ? (
                      <Skeleton variant="rounded" animation="wave" height={336} width={320} />
                    ) : (
                      <CalendarPosicoes
                        key={'data-posicoes-portfolio'}
                        id="posicoes-portfolio"
                        name="posicoes-portfolio"
                        carteiraId={formik.values.portfolio_id}
                        dataPosicao={formik.values.data}
                        datasPosicoes={datas}
                        onlyHighlightAble={true}
                        onChange={handleChangePorfolioPosicao}
                      />
                    )}
                  </Stack>
                </Stack>
              </>
            )} */}
            {datas && (
              <>
                <Stack direction="row" spacing={2}>
                  <Stack flexGrow={1} flexBasis={1}>
                    {isLoadingData ? (
                      <Skeleton variant="rounded" animation="wave" height={65} />
                    ) : (
                      <TextField
                        id="posicoes-portfolio"
                        name="data"
                        label="Posição de"
                        variant="outlined"
                        value={formik.values.data}
                        onChange={formik.handleChange}
                        sx={{
                          '& .MuiChip-root': {
                            display: 'none'
                          }
                        }}
                        select
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon iconName="calendar" size={21} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              {datas.length > 0 &&
                                !validators.isEmpty(xDataSelectedSync) &&
                                !xDataSelectedSync && (
                                  <Chip
                                    icon={<Icon iconName="warning" size={18} />}
                                    label={'Data não sincronizada'}
                                    color="warning"
                                    size="small"
                                    sx={{ px: 1, display: 'flex !important', mr: 4 }}
                                    variant="filled"
                                  />
                                )}
                            </InputAdornment>
                          )
                        }}
                        error={Boolean(formik.errors.data)}
                        helperText={formik.errors.data}
                        disabled={datas.length <= 1}>
                        {datas.map(xData => (
                          <MenuItem key={xData.data} value={xData.data}>
                            <ListItemText>
                              {formats.formatDateCustom(dates.toDate(xData.data), 'DD/MM/YYYY')}
                            </ListItemText>
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Stack>
                </Stack>
              </>
            )}

            {/* Portfolio Carteiras  */}
            {datas.length > 0 && xIsShowDetails && (
              <Stack flexGrow={1} sx={{ minHeight: 300 }}>
                {isLoadingCarteiras && (
                  <Stack flexGrow={1} flexBasis={1} justifyContent="center" alignItems="center">
                    <CircularProgress size={32} />
                  </Stack>
                )}

                {!isLoadingCarteiras && !validators.isEmpty(formik.values.carteiras) && (
                  <Stack spacing={1}>
                    <Typography color="warning.main" variant="caption" component="p">
                      <b>Data não sincronizada:</b> Indica que a data da última posição de pelo
                      menos uma carteira do portfólio é diferente da data selecionada ou que a
                      carteira não possui posições disponíveis.
                    </Typography>
                    <TableContainer
                      id="table-container"
                      sx={{
                        overflow: 'auto',
                        maxHeight: '290px',
                        paddingRight: 2,
                        paddingTop: 2
                      }}>
                      <Table size="small" tableLayout="fixed">
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <Typography variant="caption" color="textSecondary" noWrap>
                                Carteira
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="textSecondary" noWrap>
                                Tipo
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="textSecondary" noWrap>
                                Última Posição
                              </Typography>
                            </TableCell>
                            <TableCell align="center">Posição</TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {formik.values.carteiras
                            .slice()
                            .sort((xA, xB) => {
                              // Ordena por data decrescente
                              if (xA.data < xB.data) return 1;
                              if (xA.data > xB.data) return -1;
                              return 0;
                            })
                            .map(xCarteira => {
                              const { carteira, carteira_tipo_id, data, data_sync, aberta } =
                                xCarteira;

                              return (
                                <TableRow
                                  key={xCarteira.carteira_id}
                                  sx={{
                                    '&:last-child td, &:last-child th': {
                                      border: 0
                                    }
                                  }}>
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    sx={{
                                      position: 'sticky',
                                      left: 0,
                                      zIndex: 1,
                                      backgroundColor: theme => theme.palette.secondary.dark
                                    }}>
                                    <Typography color="textPrimary" variant="caption">
                                      {carteira}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="left">
                                    {CARTEIRA_TIPO[carteira_tipo_id].label}
                                  </TableCell>
                                  <TableCell align="left">
                                    {data ? formats.formatDate(data) : '--'}
                                  </TableCell>
                                  <TableCell align="center">
                                    <Tooltip
                                      title={
                                        validators.isNull(aberta)
                                          ? 'Posição Ausente'
                                          : aberta
                                          ? 'Posição Aberta'
                                          : 'Posição Fechada'
                                      }>
                                      <IconButton
                                        color={
                                          validators.isNull(aberta)
                                            ? 'error'
                                            : aberta
                                            ? 'warning'
                                            : 'primary'
                                        }
                                        size="small">
                                        <Icon
                                          iconName={
                                            validators.isNull(aberta)
                                              ? 'warning'
                                              : aberta
                                              ? 'box_3d_open'
                                              : 'box_3d'
                                          }
                                          size={18}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell align="right">
                                    {!validators.isNull(data_sync) && !data_sync && (
                                      <Tooltip title="A data da posição disponível desta carteira, não está em sincronia com a data selecionada do portfólio. Usaremos a posição mais próxima disponível da data selecionada">
                                        <IconButton color="warning" size="small">
                                          <Icon iconName={'warning'} size={18} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Stack>
                )}
              </Stack>
            )}
          </Stack>
        )}

        {!isLoadingEnquadramentos && validators.isEmpty(enquadramentos) && (
          <Stack flexGrow={1}>
            <Alert severity="warning">Necessário configurar um enquadramento para continuar.</Alert>
          </Stack>
        )}

        {(formik.values.carteira_id || formik.values.portfolio_id) &&
          !validators.isEmpty(enquadramentos) && (
            <Stack flexGrow={1}>
              <Autocomplete
                disablePortal
                options={enquadramentos}
                noOptionsText={'Nenhum Enquadramento Encontrado'}
                autoHighlight={true}
                onChange={handleChangeEnquadramento}
                value={xEnquadramentoValue}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  return (
                    <li key={crypto.randomUUID()} {...optionProps}>
                      <Stack direction="row" flexGrow={1} spacing={3} alignItems="flex-start">
                        <Stack flexGrow={1}>
                          <Typography variant="body2" color="textPrimary" sx={{ fontWeight: 600 }}>
                            {option.label}
                          </Typography>
                        </Stack>
                      </Stack>
                    </li>
                  );
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Enquadramento"
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true
                    }}
                  />
                )}
              />
            </Stack>
          )}

        {formik.values.carteira_id && (
          <Stack flexGrow={1} sx={{ marginLeft: '16px !important' }}>
            <FormControl>
              <Typography id="consolidacao_por_emissor" variant="caption" color="textSecondary">
                Consolidação por Emissor
              </Typography>
              <RadioGroup
                aria-labelledby="consolidacao_por_emissor"
                value={formik.values.consolidacao_por_emissor}
                id="consolidacao_por_emissor"
                name="consolidacao_por_emissor">
                <FormControlLabel
                  value="I"
                  control={<Radio size="small" />}
                  label="Considerar somente os ativos desta carteira"
                  onChange={formik.handleChange}
                  componentsProps={{
                    typography: {
                      variant: 'caption'
                    }
                  }}
                />
                <FormControlLabel
                  value="P"
                  control={<Radio size="small" />}
                  label="Considerar os ativos de todas as carteiras próprias"
                  onChange={formik.handleChange}
                  componentsProps={{
                    typography: {
                      variant: 'caption'
                    }
                  }}
                />
              </RadioGroup>
            </FormControl>
          </Stack>
        )}

        <Stack alignItems="center" justifyContent="flex-end" direction="row" spacing={2}>
          <Button variant="outlined" type="button" color="primary" onClick={handleClose}>
            Cancelar
          </Button>
          <LoadingButton
            variant="contained"
            type="submit"
            loading={formik.isSubmitting}
            disabled={
              formik.isSubmitting ||
              !formik.isValid ||
              (validators.isEmpty(formik.values.carteira_id) &&
                validators.isEmpty(formik.values.portfolio_id))
            }>
            Processar
          </LoadingButton>
        </Stack>
      </Stack>
    </form>
  );
});

export default withResponseHandling(withSaveParams(EnquadramentoParams));
